import React from "react";
import { Box, Typography, Chip, Divider, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import StatusChip from "../common/StatusChip";
import { castEnumForDisplay } from "../../helpers/enumHelpers";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { UserResponseDto } from "../../shared/dtos/user/UserResponseDto";

interface ViewUserModalProps {
  open: boolean;
  onClose: () => void;
  user: UserResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({
  open,
  onClose,
  user,
  loading,
  error = null,
}) => {
  const renderContent = (): React.JSX.Element => {
    if (loading) {
      return <AppCircularProgress />;
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!user) {
      return <Typography sx={{ my: 2 }}>User data not available.</Typography>;
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Full Name"
            value={`${user.firstName} ${user.lastName}`}
          />
          <DetailItem label="Username" value={user.username} />
          <DetailItem label="Email" value={user.email || "N/A"} />
          <DetailItem label="Role" value={castEnumForDisplay(user.userRole)} />
          {/* Note: Branch relationship structure usually passed to View from parent, assuming user.branchId is mapped or handled in DetailItem if needed */}
          <DetailItem label="Branch ID" value={user.branchId || "N/A"} />
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <StatusChip status={user.status} />
          </Box>
          <DetailItem
            label="Joined On"
            value={new Date(user.createdAt).toLocaleString()}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Effective Permissions ({user.permissions?.length || 0})
        </Typography>
        <Box
          sx={{
            maxHeight: 250,
            overflowY: "auto",
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.default",
          }}
        >
          {user.permissions && user.permissions.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {[...user.permissions].sort().map((p) => (
                <Chip key={p} label={p} size="small" variant="outlined" />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No permissions assigned to this user&apos;s role.
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="User Details" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewUserModal;
